import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Define color scheme
const COLORS = {
  primary: '#243444',
  secondary: '#D2BA7B',
  tertiary: '#4C5454',
  background: '#FFFAFA',
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  mediumGray: '#E0E0E0',
  darkGray: '#707070',
  red: '#7C1227'
};

// Define fonts
const FONTS = {
  heading: 'helvetica',
  body: 'helvetica'
};

/**
 * Generates and downloads a modernized PDF report from the Industry Analysis page
 * @param {Object} data - The data needed for the report
 */
export const generateModernIndustryAnalysisReport = async (data) => {
  try {
    const {
      organization,
      assessment,
      industryData,
      industryComparisons,
      aiAnalysis,
      detailedInsights,
      implementationSteps,
      itemsToConsider
    } = data;

    // Create a new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Set document properties
    doc.setProperties({
      title: `${organization?.name || 'Organization'} - Industry Analysis Report`,
      subject: 'Business Resilience Navigator Industry Analysis',
      author: 'Business Resilience Navigator',
      creator: 'Business Resilience Navigator'
    });

    // Add cover page
    addCoverPage(doc, organization, assessment);

    // Add executive summary
    addExecutiveSummary(doc, organization, assessment, industryData, aiAnalysis, detailedInsights, industryComparisons);

    // Add industry comparison section
    addIndustryComparisons(doc, industryComparisons);

    // Detailed insights section removed as requested

    // Add implementation steps section
    addImplementationSteps(doc, implementationSteps);

    // Add items to consider section
    addItemsToConsider(doc, itemsToConsider);
    
    // Add regulatory considerations section
    addRegulatoryConsiderations(doc, aiAnalysis, detailedInsights);

    // Add footer to all pages
    addFooters(doc, organization);

    // Save the PDF
    doc.save(`${organization?.name || 'Organization'}_Industry_Analysis_Report.pdf`);
    
    return true;
  } catch (error) {
    console.error('Error generating modernized PDF report:', error);
    return false;
  }
};

/**
 * Adds a modern cover page to the report
 */
const addCoverPage = (doc, organization, assessment) => {
  // Background rectangle
  doc.setFillColor(COLORS.primary);
  doc.rect(0, 0, 210, 297, 'F');
  
  // Accent diagonal stripe
  doc.setFillColor(COLORS.secondary);
  doc.rect(0, 0, 210, 80, 'F');
  
  // Title text
  doc.setTextColor(COLORS.white);
  doc.setFontSize(28);
  doc.setFont(FONTS.heading, 'bold');
  doc.text('INDUSTRY ANALYSIS', 20, 120);
  doc.text('REPORT', 20, 135);
  
  // Organization name
  doc.setFontSize(18);
  doc.setFont(FONTS.heading, 'normal');
  doc.text(organization?.name || 'Organization Name', 20, 160);
  
  // Assessment name
  doc.setFontSize(14);
  doc.setFont(FONTS.heading, 'italic');
  doc.text(assessment?.name || 'Assessment Name', 20, 170);
  
  // Date
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  doc.setFontSize(12);
  doc.setFont(FONTS.body, 'normal');
  doc.text(`Generated: ${today}`, 20, 190);
  
  // Logo placeholder
  doc.setFillColor(COLORS.white);
  doc.roundedRect(140, 240, 50, 20, 3, 3, 'F');
  doc.setTextColor(COLORS.primary);
  doc.setFontSize(10);
  doc.text('Business Resilience', 165, 250, { align: 'center' });
  doc.text('Navigator', 165, 255, { align: 'center' });
  
  // Add a new page for the content
  doc.addPage();
};

/**
 * Adds the executive summary section
 * @param {Object} doc - The jsPDF document
 * @param {Object} organization - Organization data
 * @param {Object} assessment - Assessment data
 * @param {Object} industryData - Industry data
 * @param {Object} aiAnalysis - AI analysis data
 * @param {Object} detailedInsights - Detailed insights data
 * @param {Object} industryComparisons - Industry comparisons data with overallComparison
 */
const addExecutiveSummary = (doc, organization, assessment, industryData, aiAnalysis, detailedInsights, industryComparisons) => {
  // Section header
  addModernSectionHeader(doc, 'Executive Summary', 20);
  
  // Organization and assessment info
  doc.setFontSize(12);
  doc.setFont(FONTS.body, 'bold');
  doc.setTextColor(COLORS.primary);
  doc.text('Organization:', 20, 40);
  doc.setFont(FONTS.body, 'normal');
  doc.text(organization?.name || 'Organization Name', 60, 40);
  
  doc.setFont(FONTS.body, 'bold');
  doc.text('Industry:', 20, 48);
  doc.setFont(FONTS.body, 'normal');
  doc.text(organization?.industryType || assessment?.industryType || 'Unknown', 60, 48);
  
  doc.setFont(FONTS.body, 'bold');
  doc.text('Assessment:', 20, 56);
  doc.setFont(FONTS.body, 'normal');
  doc.text(assessment?.name || 'Assessment Name', 60, 56);
  
  doc.setFont(FONTS.body, 'bold');
  doc.text('Date Completed:', 20, 64);
  doc.setFont(FONTS.body, 'normal');
  doc.text(assessment?.completedAt ? new Date(assessment.completedAt).toLocaleDateString() : 'Unknown', 60, 64);
  
  // Overall score - use the industry comparison data if available
  const score = assessment?.overallScore || 
               (industryComparisons?.overallComparison?.organizationScore) || 0;
  const normalizedScore = score < 1 ? Math.round(score * 100) : Math.round(score);
  
  // Score circle
  const scoreColor = getScoreColor(normalizedScore);
  doc.setFillColor(scoreColor);
  doc.circle(165, 50, 15, 'F');
  
  // Score text
  doc.setTextColor(COLORS.white);
  doc.setFontSize(14);
  doc.setFont(FONTS.heading, 'bold');
  doc.text(`${normalizedScore}%`, 165, 50, { align: 'center' });
  
  // Get maturity level based on score
  let maturityLevel = 'None';
  if (normalizedScore >= 91) {
    maturityLevel = 'Optimized';
  } else if (normalizedScore >= 75) {
    maturityLevel = 'Compliant';
  } else if (normalizedScore >= 50) {
    maturityLevel = 'Managed';
  } else if (normalizedScore >= 20) {
    maturityLevel = 'Defined';
  } else if (normalizedScore >= 1) {
    maturityLevel = 'Initial';
  }
  
  // Score label with maturity level
  doc.setTextColor(COLORS.primary);
  doc.setFontSize(10);
  doc.setFont(FONTS.body, 'normal');
  doc.text(`Maturity Level: ${maturityLevel}`, 165, 70, { align: 'center' });
  
  // Maturity level definitions
  let definitionsYPos = 80;
  doc.setFontSize(10);
  doc.setFont(FONTS.heading, 'bold');
  doc.setTextColor(COLORS.primary);
  doc.text('Maturity Level Definitions:', 20, definitionsYPos);
  definitionsYPos += 12;
  
  // Define the maturity levels with proper text wrapping
  const maturityLevels = [
    { name: 'None (0%)', description: 'No formal processes exist.' },
    { name: 'Initial (1-19%)', description: 'The organization is just beginning to understand resilience. Efforts are unstructured and reactive.' },
    { name: 'Defined (20-49%)', description: 'The organization has established clear processes and procedures for resilience.' },
    { name: 'Managed (50-74%)', description: 'The organization actively monitors and measures its resilience processes.' },
    { name: 'Compliant (75-90%)', description: 'The organization and its program meets industry standards and best practices.' },
    { name: 'Optimized (91-100%)', description: 'The organization not only meets standards but also continuously seeks ways to improve.' }
  ];
  
  // Create a table for maturity level definitions
  const levelColWidth = 40;
  const descColWidth = 130;
  const rowHeight = 12;
  
  // Table headers
  doc.setFillColor(COLORS.primary);
  doc.rect(20, definitionsYPos, levelColWidth, rowHeight, 'F');
  doc.rect(20 + levelColWidth, definitionsYPos, descColWidth, rowHeight, 'F');
  
  doc.setTextColor(COLORS.white);
  doc.setFont(FONTS.heading, 'bold');
  doc.text('Maturity Level', 20 + levelColWidth/2, definitionsYPos + rowHeight/2 + 2, { align: 'center' });
  doc.text('Description', 20 + levelColWidth + descColWidth/2, definitionsYPos + rowHeight/2 + 2, { align: 'center' });
  
  definitionsYPos += rowHeight;
  
  // Table rows
  for (let i = 0; i < maturityLevels.length; i++) {
    const level = maturityLevels[i];
    
    // Set alternating row background
    if (i % 2 === 0) {
      doc.setFillColor(COLORS.lightGray);
      doc.rect(20, definitionsYPos, levelColWidth + descColWidth, rowHeight, 'F');
    }
    
    // Draw cell borders
    doc.setDrawColor(COLORS.primary);
    doc.rect(20, definitionsYPos, levelColWidth, rowHeight);
    doc.rect(20 + levelColWidth, definitionsYPos, descColWidth, rowHeight);
    
    // Cell content
    doc.setTextColor(COLORS.tertiary);
    doc.setFont(FONTS.body, 'bold');
    doc.text(level.name, 20 + levelColWidth/2, definitionsYPos + rowHeight/2 + 2, { align: 'center' });
    
    doc.setFont(FONTS.body, 'normal');
    // Split description text to fit within the available width
    const descLines = doc.splitTextToSize(level.description, descColWidth - 10);
    // Only show first line in table to keep it clean
    doc.text(descLines[0], 20 + levelColWidth + 5, definitionsYPos + rowHeight/2 + 2);
    
    definitionsYPos += rowHeight;
  }
  
  // Summary text
  doc.setFontSize(11);
  doc.setTextColor(COLORS.tertiary);
  
  let summaryText = aiAnalysis?.summary || 
    `This report provides an analysis of ${organization?.name || 'your organization'}'s resilience assessment results ` +
    `compared to industry benchmarks for the ${organization?.industryType || assessment?.industryType || ''} sector. `;
  
  summaryText += `The overall maturity score is ${normalizedScore}%, which indicates ` +
    `${getMaturityLevelDescription(normalizedScore)}. `;
  
  summaryText += aiAnalysis?.recommendations || 
    'The report includes detailed insights and recommendations to improve resilience capabilities.';
  
  // Calculate the position for summary text based on where definitions end
  const summaryYPos = definitionsYPos + 10; // Add some spacing after definitions
  const summaryLines = doc.splitTextToSize(summaryText, 170);
  doc.text(summaryLines, 20, summaryYPos);
  
  // Calculate position for Detailed Analysis section
  let yPos = summaryYPos + (summaryLines.length * 5) + 10; // Add spacing after summary text
  
  // Track if we've already added the Detailed Analysis header
  let detailedAnalysisHeaderAdded = false;
  
  // Get detailed analysis from the appropriate data source
  let detailedAnalysisText = '';
  
  if (aiAnalysis && aiAnalysis.detailedAnalysis) {
    detailedAnalysisText = aiAnalysis.detailedAnalysis;
  } else if (detailedInsights && detailedInsights.analysis) {
    detailedAnalysisText = detailedInsights.analysis;
  } else {
    // Default analysis text
    detailedAnalysisText = `The organization's resilience posture requires significant improvement, with an overall score of ${normalizedScore}%. This indicates a need for targeted interventions in key resilience domains.\n\nTo enhance resilience capabilities, the organization should focus on establishing a comprehensive governance structure, implementing robust risk assessment processes, developing business continuity plans, and creating effective incident response strategies.`;
  }
  
  // Format the detailed analysis text
  const analysisLines = doc.splitTextToSize(detailedAnalysisText, 170);
  
  // Check if we need a page break before adding detailed analysis
  // Calculate how much space the analysis will take
  const analysisSpaceNeeded = analysisLines.length * 5 + 20; // Text + header + spacing
  
  // If there's not enough space on the current page, add a new page
  if (yPos + analysisSpaceNeeded > 270) {
    doc.addPage();
    yPos = 20;
  }
  
  // Add the Detailed Analysis header (only once)
  doc.setFontSize(12);
  doc.setFont(FONTS.heading, 'bold');
  doc.setTextColor(COLORS.primary);
  doc.text('Detailed Analysis', 20, yPos);
  yPos += 8;
  
  // Set text formatting for the analysis content
  doc.setFontSize(10);
  doc.setFont(FONTS.body, 'normal');
  doc.setTextColor(COLORS.tertiary);
  
  doc.text(analysisLines, 20, yPos);
  
  // Update position for Industry Trends section - reduce spacing
  yPos += analysisLines.length * 5 + 5; // Reduced spacing after detailed analysis
  
  // Check if we have enough space for Industry Trends section
  // Estimate space needed for trends section (header + at least 3 trends)
  const trendsSpaceNeeded = 50; // Header + about 3 trends with spacing
  
  // If there's not enough space on the current page, add a new page
  if (yPos + trendsSpaceNeeded > 270) {
    doc.addPage();
    yPos = 20;
  }
  
  // Industry Trends
  doc.setFontSize(12);
  doc.setFont(FONTS.heading, 'bold');
  doc.setTextColor(COLORS.primary);
  doc.text('Industry Trends', 20, yPos);
  yPos += 8;
  
  // Key Trends subheading
  doc.setFontSize(10);
  doc.setFont(FONTS.heading, 'bold');
  doc.setTextColor(COLORS.primary);
  doc.text('Key Trends', 20, yPos);
  yPos += 8;
  
  // Get trends from the appropriate data source
  let trends = [];
  
  if (industryData && Array.isArray(industryData.trends)) {
    trends = industryData.trends;
  } else if (aiAnalysis && Array.isArray(aiAnalysis.keyTrends)) {
    trends = aiAnalysis.keyTrends;
  } else if (detailedInsights && Array.isArray(detailedInsights.keyTrends)) {
    trends = detailedInsights.keyTrends;
  } else if (aiAnalysis && typeof aiAnalysis.trends === 'string') {
    // If trends is a string, split it by newlines or bullet points
    trends = aiAnalysis.trends.split(/\n|•/).filter(t => t.trim().length > 0).map(t => t.trim());
  } else {
    // Default trends
    trends = [
      'Increased focus on integrated risk management frameworks across industries.',
      'Growing importance of cybersecurity resilience as digital threats escalate.',
      'Emphasis on supply chain transparency and risk assessment due to recent global disruptions.',
      'Rising regulatory requirements for sustainability and ESG practices.',
      'Shift towards employee well-being and workforce resilience as a priority.'
    ];
  }
  
  doc.setFontSize(10);
  doc.setFont(FONTS.body, 'normal');
  doc.setTextColor(COLORS.tertiary);
  
  for (const trend of trends) {
    // Check if we need a new page
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    
    // Bullet point
    doc.setFillColor(COLORS.secondary);
    doc.circle(22, yPos - 1, 1.5, 'F');
    
    // Item text
    const lines = doc.splitTextToSize(trend, 165);
    doc.text(lines, 27, yPos);
    
    yPos += lines.length * 5 + 4;
  }
  
  // Key Industry Risks section - always start on a new page
  doc.addPage();
  yPos = 40;
  doc.setFontSize(10);
  doc.setFont(FONTS.heading, 'bold');
  doc.setTextColor(COLORS.primary);
  doc.text('Key Industry Risks', 20, yPos);
  yPos += 8;
  
  // Get industry risks from the appropriate data source
  let industryRisks = [];
  
  if (industryData && Array.isArray(industryData.risks)) {
    industryRisks = industryData.risks;
  } else if (aiAnalysis && Array.isArray(aiAnalysis.keyRisks)) {
    industryRisks = aiAnalysis.keyRisks;
  } else if (detailedInsights && Array.isArray(detailedInsights.keyRisks)) {
    industryRisks = detailedInsights.keyRisks;
  } else if (aiAnalysis && typeof aiAnalysis.industryRisks === 'string') {
    // If risks is a string, split it by newlines or bullet points
    industryRisks = aiAnalysis.industryRisks.split(/\n|•/).filter(r => r.trim().length > 0).map(r => r.trim());
  } else if (industryData && industryData.industryType) {
    // Default risks based on industry type
    const industryType = industryData.industryType || 'your industry';
    industryRisks = [
      `Regulatory compliance challenges specific to ${industryType}`,
      `Cyber threats targeting ${industryType} organizations`,
      `Supply chain disruptions affecting ${industryType}`,
      `Workforce challenges in ${industryType}`,
      `Reputational risks specific to ${industryType}`
    ];
  } else {
    // Generic risks
    industryRisks = [
      'Regulatory compliance challenges',
      'Cyber threats and vulnerabilities',
      'Supply chain disruptions',
      'Workforce challenges',
      'Reputational risks'
    ];
  }
  
  doc.setFontSize(10);
  doc.setFont(FONTS.body, 'normal');
  doc.setTextColor(COLORS.tertiary);
  
  for (const risk of industryRisks) {
    // Check if we need a new page
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    
    // Bullet point
    doc.setFillColor(COLORS.secondary);
    doc.circle(22, yPos - 1, 1.5, 'F');
    
    // Item text
    const lines = doc.splitTextToSize(risk, 165);
    doc.text(lines, 27, yPos);
    
    yPos += lines.length * 5 + 4;
  }
  
  // Improvement Opportunities section
  yPos += 10;
  doc.setFontSize(12);
  doc.setFont(FONTS.heading, 'bold');
  doc.setTextColor(COLORS.primary);
  doc.text('Improvement Opportunities', 20, yPos);
  yPos += 8;
  
  // Top Areas for Improvement subheading
  doc.setFontSize(10);
  doc.setFont(FONTS.heading, 'bold');
  doc.setTextColor(COLORS.primary);
  doc.text('Top Areas for Improvement', 20, yPos);
  yPos += 8;
  
  // Get improvement opportunities from the appropriate data source
  let improvements = [];
  
  if (aiAnalysis && Array.isArray(aiAnalysis.improvementOpportunities)) {
    improvements = aiAnalysis.improvementOpportunities;
  } else if (detailedInsights && Array.isArray(detailedInsights.areasForImprovement)) {
    improvements = detailedInsights.areasForImprovement;
  } else if (aiAnalysis && Array.isArray(aiAnalysis.areasForImprovement)) {
    improvements = aiAnalysis.areasForImprovement;
  } else if (aiAnalysis && typeof aiAnalysis.areasForImprovement === 'string') {
    // If improvements is a string, split it by newlines or bullet points
    improvements = aiAnalysis.areasForImprovement.split(/\n|•/).filter(i => i.trim().length > 0).map(i => i.trim());
  } else {
    // Default improvements
    improvements = [
      'Develop a comprehensive governance and strategy framework to guide resilience initiatives.',
      'Implement a risk management program that includes Business Impact Analysis (BIA) to understand potential threats.',
      'Establish a business continuity plan that outlines procedures for maintaining operations during disruptions.',
      'Enhance IT and cyber resilience by investing in cybersecurity measures and incident response protocols.'
    ];
  }
  
  doc.setFontSize(10);
  doc.setFont(FONTS.body, 'normal');
  doc.setTextColor(COLORS.tertiary);
  
  for (const improvement of improvements) {
    // Check if we need a new page
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    
    // Bullet point
    doc.setFillColor(COLORS.secondary);
    doc.circle(22, yPos - 1, 1.5, 'F');
    
    // Item text
    const lines = doc.splitTextToSize(improvement, 165);
    doc.text(lines, 27, yPos);
    
    yPos += lines.length * 5 + 4;
  }
  
  // Add a page break
  doc.addPage();
};

/**
 * Returns a color based on the score
 */
const getScoreColor = (score) => {
  if (score >= 90) return '#27AE60'; // Green
  if (score >= 75) return '#2980B9'; // Blue
  if (score >= 50) return COLORS.primary; // Primary
  if (score >= 20) return COLORS.secondary; // Secondary
  if (score >= 1) return '#D35400'; // Orange
  return COLORS.red; // Red
};

/**
 * Returns a description of the maturity level based on the score
 */
const getMaturityLevelDescription = (score) => {
  if (score >= 90) return 'an optimized level of maturity';
  if (score >= 75) return 'a compliant level of maturity';
  if (score >= 50) return 'a managed level of maturity';
  if (score >= 20) return 'a defined level of maturity';
  if (score >= 1) return 'an initial level of maturity';
  return 'no defined maturity';
};

/**
 * Adds a modern section header
 */
const addModernSectionHeader = (doc, title, yPos) => {
  // Background rectangle
  doc.setFillColor(COLORS.primary);
  doc.roundedRect(10, yPos, 190, 10, 2, 2, 'F');
  
  // Title text
  doc.setTextColor(COLORS.white);
  doc.setFontSize(14);
  doc.setFont(FONTS.heading, 'bold');
  doc.text(title, 20, yPos + 6.5);
  
  return yPos + 14;
};

/**
 * Adds footers to all pages
 */
const addFooters = (doc, organization) => {
  const pageCount = doc.internal.getNumberOfPages();
  
  // Skip the cover page (page 1)
  for (let i = 2; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Bottom border
    doc.setDrawColor(COLORS.secondary);
    doc.setLineWidth(0.5);
    doc.line(10, 285, 200, 285);
    
    // Footer text
    doc.setFontSize(8);
    doc.setTextColor(COLORS.tertiary);
    doc.setFont(FONTS.body, 'normal');
    
    // Left side - organization name
    doc.text(organization?.name || 'Organization', 12, 290);
    
    // Center - copyright
    doc.text('© Business Resilience Navigator', 105, 290, { align: 'center' });
    
    // Right side - page number
    doc.text(`Page ${i-1} of ${pageCount-1}`, 198, 290, { align: 'right' });
  }
};

/**
 * Adds the industry comparison section
 */
const addIndustryComparisons = (doc, industryComparisons) => {
  // Section header
  addModernSectionHeader(doc, 'Industry Comparisons', 20);
  
  if (!industryComparisons || !industryComparisons.categoryComparisons || industryComparisons.categoryComparisons.length === 0) {
    doc.setFontSize(11);
    doc.setTextColor(COLORS.tertiary);
    doc.text('No industry comparison data available.', 20, 40);
    return;
  }
  
  let yPos = 35;
  
  // Introduction text
  doc.setFontSize(11);
  doc.setTextColor(COLORS.tertiary);
  const introText = doc.splitTextToSize('The chart below shows how your organization compares to industry benchmarks across key categories.', 170);
  doc.text(introText, 20, yPos);
  yPos += 10;
  
  // Add legend
  doc.setFillColor(COLORS.primary);
  doc.rect(20, yPos, 10, 5, 'F');
  doc.setFontSize(9);
  doc.setTextColor(COLORS.tertiary);
  doc.text('Your Score', 35, yPos + 4);
  
  doc.setFillColor(COLORS.secondary);
  doc.rect(80, yPos, 10, 5, 'F');
  doc.text('Industry Average', 95, yPos + 4);
  yPos += 12;
  
  // Draw comparison bars
  const categories = industryComparisons.categoryComparisons;
  const barHeight = 12;
  const barSpacing = 8;
  const maxBarWidth = 140;
  
  for (const category of categories) {
    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    // Category name
    doc.setFontSize(10);
    doc.setFont(FONTS.body, 'bold');
    doc.setTextColor(COLORS.primary);
    doc.text(category.category, 20, yPos);
    
    // Your score bar
    const yourScore = category.score || 0;
    const yourScoreWidth = (yourScore / 100) * maxBarWidth;
    doc.setFillColor(COLORS.primary);
    doc.roundedRect(20, yPos + 4, yourScoreWidth, barHeight, 1, 1, 'F');
    
    // Industry average bar
    const industryAvg = category.industryAverage || 0;
    const industryAvgWidth = (industryAvg / 100) * maxBarWidth;
    doc.setFillColor(COLORS.secondary);
    doc.roundedRect(20, yPos + 4 + barHeight + 2, industryAvgWidth, barHeight, 1, 1, 'F');
    
    // Score labels
    doc.setFontSize(9);
    doc.setFont(FONTS.body, 'normal');
    
    // Your score label - centered in the bar (both horizontally and vertically)
    const yourScoreText = `${Math.round(yourScore)}%`;
    if (yourScoreWidth > 20) {
      doc.setTextColor(COLORS.white);
      const yourScoreX = 20 + (yourScoreWidth / 2);
      // Exact vertical center of the bar
      const yourScoreY = yPos + 4 + (barHeight / 2);
      // Bold the score text
      doc.setFont(FONTS.body, 'bold');
      doc.text(yourScoreText, yourScoreX, yourScoreY, { align: 'center', baseline: 'middle' });
      doc.setFont(FONTS.body, 'normal');
    } else {
      doc.setTextColor(COLORS.primary);
      // Bold the score text
      doc.setFont(FONTS.body, 'bold');
      doc.text(yourScoreText, yourScoreWidth + 25, yPos + 4 + (barHeight / 2), { baseline: 'middle' });
      doc.setFont(FONTS.body, 'normal');
    }
    
    // Industry average label - centered in the bar (both horizontally and vertically)
    const industryAvgText = `${Math.round(industryAvg)}%`;
    if (industryAvgWidth > 20) {
      doc.setTextColor(COLORS.white);
      const industryAvgX = 20 + (industryAvgWidth / 2);
      // Exact vertical center of the bar
      const industryAvgY = yPos + 4 + barHeight + 2 + (barHeight / 2);
      // Bold the score text
      doc.setFont(FONTS.body, 'bold');
      doc.text(industryAvgText, industryAvgX, industryAvgY, { align: 'center', baseline: 'middle' });
      doc.setFont(FONTS.body, 'normal');
    } else {
      doc.setTextColor(COLORS.secondary);
      // Bold the score text
      doc.setFont(FONTS.body, 'bold');
      doc.text(industryAvgText, industryAvgWidth + 25, yPos + 4 + barHeight + 2 + (barHeight / 2), { baseline: 'middle' });
      doc.setFont(FONTS.body, 'normal');
    }
    
    yPos += barHeight * 2 + barSpacing + 10;
  }
  
  // Add gap before next section
  yPos += 10;
  
  // Add a page break
  doc.addPage();
};

/**
 * Adds the detailed insights section
 */
const addDetailedInsights = (doc, detailedInsights, aiAnalysis, industryComparisons) => {
  // Section header
  addModernSectionHeader(doc, 'Detailed Insights', 20);
  
  let yPos = 40;
  
  // Introduction
  doc.setFontSize(11);
  doc.setTextColor(COLORS.tertiary);
  const introText = 'Based on your assessment results, the following insights have been generated:';
  doc.text(introText, 20, yPos);
  yPos += 15;
  
  // Maturity level
  const maturityLevel = detailedInsights?.maturityLevel || 
                     (industryComparisons?.overallComparison?.organizationScore ? 
                      getMaturityLevelDescription(industryComparisons.overallComparison.organizationScore) : 'Unknown');
  const maturityScore = detailedInsights?.maturityScore || 
                     industryComparisons?.overallComparison?.organizationScore || 0;
  
  // Maturity level card
  doc.setFillColor(COLORS.lightGray);
  doc.roundedRect(20, yPos, 170, 40, 3, 3, 'F');
  
  // Maturity level header
  doc.setFillColor(COLORS.primary);
  doc.roundedRect(20, yPos, 170, 10, 3, 3, 'F');
  doc.setTextColor(COLORS.white);
  doc.setFontSize(12);
  doc.setFont(FONTS.heading, 'bold');
  doc.text('Current Maturity Level', 105, yPos + 7, { align: 'center' });
  
  // Maturity level content
  doc.setTextColor(COLORS.primary);
  doc.setFontSize(14);
  doc.setFont(FONTS.heading, 'bold');
  doc.text(maturityLevel, 105, yPos + 25, { align: 'center' });
  
  // Maturity score
  const scoreColor = getScoreColor(maturityScore);
  doc.setFillColor(scoreColor);
  doc.circle(165, yPos + 25, 10, 'F');
  doc.setTextColor(COLORS.white);
  doc.setFontSize(10);
  doc.text(`${Math.round(maturityScore)}%`, 165, yPos + 25, { align: 'center' });
  
  yPos += 50;
  
  // Check if we need a new page
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }
  
  yPos += 15;
  
  // Regulatory considerations
  doc.setFontSize(12);
  doc.setFont(FONTS.heading, 'bold');
  doc.setTextColor(COLORS.primary);
  doc.text('Regulatory Considerations', 20, yPos);
  yPos += 8;
  
  const regulatory = aiAnalysis?.regulatoryConsiderations || 
    detailedInsights?.regulatoryConsiderations || 
    'No regulatory considerations available.';
  
  doc.setFontSize(10);
  doc.setFont(FONTS.body, 'normal');
  doc.setTextColor(COLORS.tertiary);
  
  const regulatoryLines = doc.splitTextToSize(regulatory, 170);
  doc.text(regulatoryLines, 20, yPos);
  
  // Add a page break
  doc.addPage();
};

/**
 * Adds the implementation steps section
 */
const addImplementationSteps = (doc, implementationSteps) => {
  // Section header - no intro text here, it will be added in createStepsTable
  addModernSectionHeader(doc, 'Implementation Roadmap', 20);
  
  let yPos = 40;
  
  // No introduction text here - it will be added in createStepsTable function
  
  // Organize implementation steps by timeframe
  const organizedSteps = {
    short: [],
    medium: [],
    long: []
  };
  
  // Process implementation steps if provided
  if (implementationSteps && implementationSteps.length > 0) {
    // First, categorize all steps by timeframe
    implementationSteps.forEach(step => {
      const timeframe = step.timeframe ? step.timeframe.toLowerCase() : '';
      const category = step.category || '';
      
      // Determine the correct category based on timeframe or explicit category
      if (category === 'shortTerm' || category === 'short' || timeframe.includes('short')) {
        organizedSteps.short.push(step);
      } else if (category === 'mediumTerm' || category === 'medium' || timeframe.includes('medium')) {
        organizedSteps.medium.push(step);
      } else if (category === 'longTerm' || category === 'long' || timeframe.includes('long')) {
        organizedSteps.long.push(step);
      } else {
        // Default to short term if no clear category
        organizedSteps.short.push(step);
      }
    });
  }
  
  // Ensure we have exactly 5 steps for each timeframe
  const defaultSteps = {
    short: [
      {
        step: 'Conduct a thorough risk assessment to identify vulnerabilities and threats.',
        timeframe: 'Short Term (0-3 months)',
        resources: 'Varies',
        category: 'short'
      },
      {
        step: 'Develop a governance framework that aligns with organizational objectives and resilience goals.',
        timeframe: 'Short Term (0-3 months)',
        resources: 'Varies',
        category: 'short'
      },
      {
        step: 'Establish a crisis communication plan and notification procedures',
        timeframe: 'Short Term (0-3 months)',
        resources: 'Varies',
        category: 'short'
      },
      {
        step: 'Implement basic business continuity measures for critical functions',
        timeframe: 'Short Term (0-3 months)',
        resources: 'Varies',
        category: 'short'
      },
      {
        step: 'Create a resilience awareness program for key stakeholders',
        timeframe: 'Short Term (0-3 months)',
        resources: 'Varies',
        category: 'short'
      }
    ],
    medium: [
      {
        step: 'Develop comprehensive business continuity plans for all critical business functions',
        timeframe: 'Medium Term (3-12 months)',
        resources: 'Medium',
        category: 'medium'
      },
      {
        step: 'Implement a formal incident management system with defined roles and responsibilities',
        timeframe: 'Medium Term (3-12 months)',
        resources: 'Medium',
        category: 'medium'
      },
      {
        step: 'Establish regular testing and exercise schedules for resilience plans',
        timeframe: 'Medium Term (3-12 months)',
        resources: 'Medium',
        category: 'medium'
      },
      {
        step: 'Integrate resilience considerations into procurement and vendor management',
        timeframe: 'Medium Term (3-12 months)',
        resources: 'Medium',
        category: 'medium'
      },
      {
        step: 'Develop cross-functional resilience teams and define responsibilities',
        timeframe: 'Medium Term (3-12 months)',
        resources: 'Medium',
        category: 'medium'
      }
    ],
    long: [
      {
        step: 'Integrate resilience considerations into strategic planning and investment decisions',
        timeframe: 'Long Term (12+ months)',
        resources: 'High',
        category: 'long'
      },
      {
        step: 'Develop a comprehensive supply chain resilience program',
        timeframe: 'Long Term (12+ months)',
        resources: 'High',
        category: 'long'
      },
      {
        step: 'Establish a continuous improvement process for resilience capabilities',
        timeframe: 'Long Term (12+ months)',
        resources: 'Medium',
        category: 'long'
      },
      {
        step: 'Create resilience innovation programs to address emerging risks',
        timeframe: 'Long Term (12+ months)',
        resources: 'High',
        category: 'long'
      },
      {
        step: 'Build resilience partnerships with external stakeholders and industry groups',
        timeframe: 'Long Term (12+ months)',
        resources: 'Medium',
        category: 'long'
      }
    ]
  };
  
  // Fill in missing steps to ensure exactly 5 for each timeframe
  ['short', 'medium', 'long'].forEach(timeframe => {
    // If we have fewer than 5 steps, add default steps
    while (organizedSteps[timeframe].length < 5) {
      const defaultIndex = organizedSteps[timeframe].length;
      if (defaultIndex < defaultSteps[timeframe].length) {
        organizedSteps[timeframe].push(defaultSteps[timeframe][defaultIndex]);
      } else {
        // If we've exhausted default steps, create a generic one
        organizedSteps[timeframe].push({
          step: `Additional ${timeframe}-term action for business resilience`,
          timeframe: timeframe === 'short' ? 'Short Term (0-3 months)' : 
                    timeframe === 'medium' ? 'Medium Term (3-12 months)' : 
                    'Long Term (12+ months)',
          resources: 'Varies',
          category: timeframe
        });
      }
    }
    
    // If we have more than 5 steps, keep only the first 5
    if (organizedSteps[timeframe].length > 5) {
      organizedSteps[timeframe] = organizedSteps[timeframe].slice(0, 5);
    }
  });
  
  // Flatten the organized steps back into a single array for processing
  const finalSteps = [
    ...organizedSteps.short,
    ...organizedSteps.medium,
    ...organizedSteps.long
  ];
  
  // Function to create a table for implementation steps
  const createStepsTable = (steps, tableTitle, startYPos) => {
    let tableYPos = startYPos;
    
    // Add extra spacing for the introduction text if this is the first table
    if (tableTitle.includes('Short Term')) {
      // Add the introduction text with proper spacing
      doc.setFontSize(11);
      doc.setTextColor(COLORS.tertiary);
      const introText = doc.splitTextToSize('Based on your organization\'s current maturity level, we recommend the following implementation steps:', 170);
      doc.text(introText, 20, tableYPos);
      tableYPos += 25; // Add significant spacing after the intro text
    }
    
    // Table title
    doc.setFontSize(11);
    doc.setFont(FONTS.heading, 'bold');
    doc.setTextColor(COLORS.primary);
    doc.text(tableTitle, 20, tableYPos);
    tableYPos += 10;
    
    // Check if we need a new page
    if (tableYPos > 240) {
      doc.addPage();
      tableYPos = 40;
      doc.text(tableTitle, 20, tableYPos);
      tableYPos += 10;
    }
    
    // Table headers
    doc.setFillColor(COLORS.primary);
    doc.rect(20, tableYPos, 20, 10, 'F');
    doc.rect(40, tableYPos, 80, 10, 'F');
    doc.rect(120, tableYPos, 35, 10, 'F');
    doc.rect(155, tableYPos, 35, 10, 'F');
    
    doc.setTextColor(COLORS.white);
    doc.setFontSize(9);
    doc.setFont(FONTS.heading, 'bold');
    doc.text('Step', 30, tableYPos + 7, { align: 'center' });
    doc.text('Action', 80, tableYPos + 7, { align: 'center' });
    doc.text('Timeframe', 137, tableYPos + 7, { align: 'center' });
    doc.text('Resources', 172, tableYPos + 7, { align: 'center' });
    
    tableYPos += 10;
    
    // Table rows
    const rowHeight = 20;
    
    // Loop through steps
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      
      // Check if we need a new page
      if (tableYPos > 240) {
        doc.addPage();
        tableYPos = 40;
        
        // Recreate table headers on new page
        doc.setFillColor(COLORS.primary);
        doc.rect(20, tableYPos, 20, 10, 'F');
        doc.rect(40, tableYPos, 80, 10, 'F');
        doc.rect(120, tableYPos, 35, 10, 'F');
        doc.rect(155, tableYPos, 35, 10, 'F');
        
        doc.setTextColor(COLORS.white);
        doc.setFontSize(9);
        doc.setFont(FONTS.heading, 'bold');
        doc.text('Step', 30, tableYPos + 7, { align: 'center' });
        doc.text('Action', 80, tableYPos + 7, { align: 'center' });
        doc.text('Timeframe', 137, tableYPos + 7, { align: 'center' });
        doc.text('Resources', 172, tableYPos + 7, { align: 'center' });
        
        tableYPos += 10;
      }
      
      // Set alternating row background
      if (i % 2 === 0) {
        doc.setFillColor(COLORS.lightGray);
        doc.rect(20, tableYPos, 170, rowHeight, 'F');
      }
      
      // Draw cell borders
      doc.setDrawColor(COLORS.primary);
      doc.rect(20, tableYPos, 20, rowHeight);
      doc.rect(40, tableYPos, 80, rowHeight);
      doc.rect(120, tableYPos, 35, rowHeight);
      doc.rect(155, tableYPos, 35, rowHeight);
      
      // Cell content
      doc.setTextColor(COLORS.tertiary);
      doc.setFontSize(9);
      doc.setFont(FONTS.body, 'normal');
      
      // Step number - vertically centered
      doc.setFont(FONTS.body, 'bold'); // Bold the step number
      doc.text(`${i+1}`, 30, tableYPos + rowHeight/2, { align: 'center', baseline: 'middle' });
      doc.setFont(FONTS.body, 'normal');
      
      // Step action - with text wrapping and better vertical alignment
      // Remove any "Step X:" prefix from the step text
      let stepText = step.step;
      stepText = stepText.replace(/^Step\s+\d+:\s*/i, ''); // Remove "Step X:" prefix
      
      const actionLines = doc.splitTextToSize(stepText, 75);
      const limitedActionLines = actionLines.slice(0, 2); // Limit to 2 lines
      if (actionLines.length > 2) {
        limitedActionLines[1] = limitedActionLines[1].substring(0, limitedActionLines[1].length - 3) + '...';
      }
      
      // Calculate vertical position based on number of lines
      const actionY = tableYPos + (rowHeight - (limitedActionLines.length * 5)) / 2;
      doc.text(limitedActionLines, 45, actionY);
      
      // Timeframe - align center both horizontally and vertically
      const timeframeLines = doc.splitTextToSize(step.timeframe || 'Not specified', 30);
      const timeframeY = tableYPos + rowHeight/2;
      doc.text(timeframeLines, 137, timeframeY, { align: 'center', baseline: 'middle' });
      
      // Resources - align center both horizontally and vertically
      const resourcesLines = doc.splitTextToSize(step.resources || 'Not specified', 30);
      // Calculate vertical position based on number of lines
      const resourcesY = tableYPos + rowHeight/2;
      doc.text(resourcesLines, 172, resourcesY, { align: 'center', baseline: 'middle' });
      
      tableYPos += rowHeight;
    }
    
    return tableYPos + 10; // Return the new Y position with some spacing
  };
  
  // Create tables for each timeframe using the organized steps
  const shortTermSteps = organizedSteps.short;
  const mediumTermSteps = organizedSteps.medium;
  const longTermSteps = organizedSteps.long;
  
  // Start with the implementation roadmap section - add more spacing for the introduction
  yPos = 60;
  
  // Create the short term steps table
  yPos = createStepsTable(shortTermSteps, 'Short Term Actions (0-3 months)', yPos);
  
  // Create the medium term steps table
  yPos = createStepsTable(mediumTermSteps, 'Medium Term Actions (3-12 months)', yPos);
  
  // Create the long term steps table
  yPos = createStepsTable(longTermSteps, 'Long Term Actions (12+ months)', yPos);
  
  // Add spacing after all tables
  yPos += 20;
  
  // Add a page break
  doc.addPage();
};

/**
 * Adds the regulatory considerations section
 */
const addRegulatoryConsiderations = (doc, aiAnalysis, detailedInsights) => {
  // Add a page break before starting the regulatory considerations section
  doc.addPage();
  
  // Section header
  addModernSectionHeader(doc, 'Industry-Specific Regulatory Considerations', 20);
  
  let yPos = 40;
  
  // Get regulatory considerations from either aiAnalysis or detailedInsights
  const regulatoryConsiderations = 
    (aiAnalysis && aiAnalysis.regulatoryConsiderations) || 
    (detailedInsights && detailedInsights.regulatoryConsiderations) || 
    {};
  
  // Default text if no regulatory considerations are provided
  let regulatoryText = 'The organization must comply with various regulatory frameworks that govern operational resilience, data protection, and cybersecurity. Key regulations may include GDPR for data privacy, ISO standards for risk management and business continuity, and industry-specific regulations that mandate resilience practices. Non-compliance could lead to significant legal repercussions and financial penalties. It is crucial to stay updated on regulatory changes and ensure alignment with best practices to mitigate risks.';
  
  // If we have detailed regulatory considerations, use those
  if (regulatoryConsiderations.overview) {
    regulatoryText = regulatoryConsiderations.overview;
  }
  
  // Add the regulatory text
  doc.setFontSize(10);
  doc.setTextColor(COLORS.tertiary);
  doc.setFont(FONTS.body, 'normal');
  
  const textLines = doc.splitTextToSize(regulatoryText, 170);
  doc.text(textLines, 20, yPos);
  yPos += textLines.length * 6 + 10;
  
  // Add key regulations if available
  if (regulatoryConsiderations.keyRegulations && regulatoryConsiderations.keyRegulations.length > 0) {
    doc.setFont(FONTS.body, 'bold');
    doc.text('Key Regulations:', 20, yPos);
    yPos += 8;
    
    doc.setFont(FONTS.body, 'normal');
    const bulletChar = '•';
    const bulletIndent = 5;
    const textIndent = 15;
    const lineSpacing = 6;
    
    // Loop through key regulations
    for (let i = 0; i < Math.min(regulatoryConsiderations.keyRegulations.length, 8); i++) {
      const regulation = regulatoryConsiderations.keyRegulations[i];
      
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 40;
        
        // Reset text style
        doc.setTextColor(COLORS.tertiary);
        doc.setFontSize(10);
        doc.setFont(FONTS.body, 'bold');
        doc.text('Key Regulations (continued):', 20, yPos);
        yPos += 8;
        doc.setFont(FONTS.body, 'normal');
      }
      
      // Add bullet point
      doc.text(bulletChar, 20 + bulletIndent, yPos);
      
      // Format and add the regulation text with wrapping
      const maxWidth = 170 - textIndent;
      const regText = typeof regulation === 'string' ? regulation : JSON.stringify(regulation);
      const textLines = doc.splitTextToSize(regText, maxWidth);
      doc.text(textLines, 20 + textIndent, yPos);
      
      // Increase yPos based on number of lines
      yPos += lineSpacing * textLines.length + 2;
    }
    
    yPos += 6;
  }
  
  // Add compliance requirements if available
  if (regulatoryConsiderations.complianceRequirements) {
    // Check if we need a new page
    if (yPos > 220) {
      doc.addPage();
      yPos = 40;
    }
    
    doc.setFont(FONTS.body, 'bold');
    doc.text('Compliance Requirements:', 20, yPos);
    yPos += 8;
    
    doc.setFont(FONTS.body, 'normal');
    const complianceText = regulatoryConsiderations.complianceRequirements;
    const complianceLines = doc.splitTextToSize(complianceText, 170);
    doc.text(complianceLines, 20, yPos);
    yPos += complianceLines.length * 6 + 10;
  }
  
  // Add best practices if available
  if (regulatoryConsiderations.bestPractices) {
    // Check if we need a new page
    if (yPos > 220) {
      doc.addPage();
      yPos = 40;
    }
    
    doc.setFont(FONTS.body, 'bold');
    doc.text('Best Practices:', 20, yPos);
    yPos += 8;
    
    doc.setFont(FONTS.body, 'normal');
    const practicesText = regulatoryConsiderations.bestPractices;
    const practicesLines = doc.splitTextToSize(practicesText, 170);
    doc.text(practicesLines, 20, yPos);
  }
  
  // Add a page break
  doc.addPage();
};

/**
 * Adds the items to consider section
 */
const addItemsToConsider = (doc, itemsToConsider) => {
  // Section header
  addModernSectionHeader(doc, 'Items to Consider', 20);
  
  let yPos = 40;
  
  // Introduction
  doc.setFontSize(11);
  doc.setTextColor(COLORS.tertiary);
  const introText = 'Consider the following items to further enhance your organization\'s resilience capabilities:';
  const introLines = doc.splitTextToSize(introText, 170);
  doc.text(introLines, 20, yPos);
  yPos += 15;
  
  if (!itemsToConsider || itemsToConsider.length === 0) {
    // Use default items if none are provided
    itemsToConsider = [
      'Engage stakeholders across the organization to ensure buy-in for resilience initiatives.',
      'Utilize external consultants or experts to guide the development of resilience frameworks.',
      'Develop communication strategies to inform employees about resilience efforts and their roles.',
      'Invest in training and awareness programs to build a culture of resilience within the organization.',
      'Regularly review and update resilience plans to adapt to changing risks and business environments.'
    ];
  }
  
  // Create a bulleted list for items to consider
  doc.setTextColor(COLORS.tertiary);
  doc.setFontSize(10);
  doc.setFont(FONTS.body, 'normal');
  
  // Calculate the bullet character and spacing
  const bulletChar = '•';
  const bulletIndent = 5;
  const textIndent = 15;
  const lineSpacing = 8;
  
  // Process items to ensure they're in a consistent format
  const processedItems = itemsToConsider.map(item => {
    // If item is a string, use it directly
    if (typeof item === 'string') {
      return item;
    }
    // If item is an object with title, item, or description property, use that
    else if (typeof item === 'object') {
      return item.title || item.item || item.description || JSON.stringify(item);
    }
    // Fallback
    return String(item);
  });
  
  // Loop through items to consider (up to 8 items)
  for (let i = 0; i < Math.min(processedItems.length, 8); i++) {
    const itemText = processedItems[i];
    
    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      yPos = 40;
      
      // Repeat the section title on the new page
      doc.setFontSize(11);
      doc.setTextColor(COLORS.tertiary);
      doc.text('Items to Consider (continued):', 20, yPos);
      yPos += 15;
      
      // Reset text style
      doc.setTextColor(COLORS.tertiary);
      doc.setFontSize(10);
      doc.setFont(FONTS.body, 'normal');
    }
    
    // Add bullet point
    doc.text(bulletChar, 20 + bulletIndent, yPos);
    
    // Format and add the item text with wrapping
    const maxWidth = 170 - textIndent;
    const textLines = doc.splitTextToSize(itemText, maxWidth);
    doc.text(textLines, 20 + textIndent, yPos);
    
    // Increase yPos based on number of lines in this item
    yPos += lineSpacing * textLines.length + 4; // Add extra spacing between items
  }
  
  // Update yPos based on the number of rows needed for the items
  yPos += 10;
};

/**
 * Adds a modern bullet list
 */
const addModernBulletList = (doc, items, yPos) => {
  doc.setFontSize(10);
  doc.setFont(FONTS.body, 'normal');
  doc.setTextColor(COLORS.tertiary);
  
  let currentY = yPos;
  
  for (const item of items) {
    // Check if we need a new page
    if (currentY > 270) {
      doc.addPage();
      currentY = 20;
    }
    
    // Bullet point
    doc.setFillColor(COLORS.secondary);
    doc.circle(22, currentY - 1, 1.5, 'F');
    
    // Item text
    const lines = doc.splitTextToSize(item, 165);
    doc.text(lines, 27, currentY);
    
    currentY += lines.length * 5 + 4;
  }
  
  return currentY;
};

// Export the original function for backward compatibility
export { generateIndustryAnalysisReport } from './reportGenerator';
